import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Plus, 
  Receipt, 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  Calendar,
  FileText,
  Upload,
  Send,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  ArrowLeft,
  Trash2,
  Edit,
  Download,
  Calculator,
  Wallet,
  Building2,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Eye,
  Bot,
  Sparkles,
  ScrollText,
  PieChart,
  BarChart3,
  FileSpreadsheet,
  Home,
  Users,
  Scale,
  Hammer,
  FileCheck,
  Landmark,
  Banknote
} from "lucide-react";

const AI_REPORT_TYPES = [
  { value: "general_ledger", label: "General Ledger", icon: ScrollText, description: "Complete chronological record of all transactions" },
  { value: "net_cash_flow", label: "Net Cash Flow", icon: TrendingUp, description: "Analysis of cash inflows and outflows" },
  { value: "income_statement", label: "Income Statement", icon: PieChart, description: "Revenue and expenses summary" },
  { value: "rent_roll_stress_test", label: "Rent Roll Stress Test", icon: BarChart3, description: "Rental income stability analysis" },
  { value: "tax_package", label: "Tax Package", icon: FileSpreadsheet, description: "Comprehensive tax preparation docs" },
  { value: "tenant_ledger", label: "Tenant Ledger", icon: Users, description: "Individual tenant payment history" },
  { value: "balance_sheet", label: "Balance Sheet", icon: Scale, description: "Assets, liabilities, and equity" },
  { value: "capital_expenses", label: "Capital Expenses", icon: Hammer, description: "Major improvements tracking" },
  { value: "schedule_e", label: "Schedule E (IRS)", icon: FileCheck, description: "IRS Form for rental income" },
  { value: "real_estate_owned", label: "Real Estate Owned", icon: Home, description: "Complete portfolio overview" },
  { value: "escrow_ledger", label: "Escrow Ledger", icon: Banknote, description: "Security deposits and reserves" }
];

const TRANSACTION_CATEGORIES = {
  income: [
    { value: "rent", label: "Rent Payment" },
    { value: "late_fee", label: "Late Fee" },
    { value: "security_deposit", label: "Security Deposit" },
    { value: "parking", label: "Parking Fee" },
    { value: "pet_fee", label: "Pet Fee" },
    { value: "laundry", label: "Laundry Income" },
    { value: "other", label: "Other Income" }
  ],
  expense: [
    { value: "property_tax", label: "Property Tax" },
    { value: "water", label: "Water" },
    { value: "electric", label: "Electric" },
    { value: "gas", label: "Gas" },
    { value: "garbage", label: "Garbage/Trash" },
    { value: "sewer", label: "Sewer" },
    { value: "common_area_repair", label: "Common Area Repair" },
    { value: "maintenance", label: "Maintenance" },
    { value: "repairs", label: "Unit Repairs" },
    { value: "landscaping", label: "Landscaping" },
    { value: "pest_control", label: "Pest Control" },
    { value: "insurance", label: "Insurance" },
    { value: "hoa_fee", label: "HOA Fee" },
    { value: "management_fee", label: "Management Fee" },
    { value: "cleaning", label: "Cleaning" },
    { value: "supplies", label: "Supplies" },
    { value: "legal", label: "Legal Fees" },
    { value: "advertising", label: "Advertising" },
    { value: "mortgage", label: "Mortgage Payment" },
    { value: "capital_improvement", label: "Capital Improvement" },
    { value: "other", label: "Other Expense" }
  ]
};

const PAYMENT_METHODS = [
  { value: "zelle", label: "Zelle" },
  { value: "cash", label: "Cash" },
  { value: "check", label: "Check" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "credit_card", label: "Credit Card" },
  { value: "other", label: "Other" }
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export default function RentLedger() {
  const { propertyId, unitId } = useParams();
  const { api } = useAuth();
  
  const [property, setProperty] = useState(null);
  const [unit, setUnit] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("ledger");
  
  // Dialog states
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  
  // Form states
  const [transactionForm, setTransactionForm] = useState({
    transaction_type: "income",
    category: "rent",
    amount: "",
    description: "",
    date: new Date().toISOString().split('T')[0],
    payment_method: "zelle",
    vendor: "",
    is_tax_deductible: false,
    notes: ""
  });
  
  // Receipt OCR
  const [receiptUrl, setReceiptUrl] = useState("");
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  
  // Rent config
  const [rentConfig, setRentConfig] = useState({
    rent_amount: "",
    due_day: "1",
    tenant_name: "",
    tenant_email: "",
    tenant_phone: ""
  });
  
  // Late fee config
  const [lateFeeConfig, setLateFeeConfig] = useState({
    grace_period_days: "5",
    late_fee_type: "flat",
    late_fee_amount: "50",
    max_late_fee: ""
  });
  
  // P&L Report
  const [reportData, setReportData] = useState(null);
  const [reportStartDate, setReportStartDate] = useState(`${new Date().getFullYear()}-01-01`);
  const [reportEndDate, setReportEndDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Calendar
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth() + 1);
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [calendarEvents, setCalendarEvents] = useState([]);
  
  // Bank reconciliation
  const [bankBalance, setBankBalance] = useState("");
  const [reconciliationData, setReconciliationData] = useState(null);
  
  // AI Reports
  const [selectedReportType, setSelectedReportType] = useState("");
  const [aiReportLoading, setAiReportLoading] = useState(false);
  const [aiReportData, setAiReportData] = useState(null);
  const [aiReportStartDate, setAiReportStartDate] = useState(`${new Date().getFullYear()}-01-01`);
  const [aiReportEndDate, setAiReportEndDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Property units for switching
  const [allUnits, setAllUnits] = useState([]);
  const [selectedUnitId, setSelectedUnitId] = useState(unitId || "all");
  
  // Rent Roll
  const [rentRoll, setRentRoll] = useState(null);
  const [rentRollYear, setRentRollYear] = useState(new Date().getFullYear());
  const [rentRollLoading, setRentRollLoading] = useState(false);
  const [depositsData, setDepositsData] = useState(null);
  
  // Edit Unit Dialog
  const [editUnitDialogOpen, setEditUnitDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [editUnitForm, setEditUnitForm] = useState({
    unit_number: "",
    rent_amount: "",
    lease_start: "",
    lease_end: "",
    deposit: "",
    deposit_held: true,
    tenant_name: "",
    tenant_email: "",
    tenant_phone: "",
    is_occupied: false,
    rent_due_day: "1",
    late_fee_amount: "50",
    grace_period_days: "5"
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch property
      const propRes = await api.get(`/properties/${propertyId}`);
      setProperty(propRes.data);
      
      // Fetch ALL units for this property
      const unitsRes = await api.get(`/units?property_id=${propertyId}`);
      setAllUnits(unitsRes.data || []);
      
      // Set current unit if unitId provided
      if (unitId) {
        const foundUnit = unitsRes.data.find(u => u.id === unitId);
        setUnit(foundUnit);
        setSelectedUnitId(unitId);
      } else {
        setSelectedUnitId("all");
      }
      
      // Fetch transactions based on selected unit
      let url = `/ledger/transactions?property_id=${propertyId}`;
      const activeUnitId = unitId || (selectedUnitId !== "all" ? selectedUnitId : null);
      if (activeUnitId && activeUnitId !== "all") url += `&unit_id=${activeUnitId}`;
      const transRes = await api.get(url);
      setTransactions(transRes.data);
      
      // Fetch rent config
      if (unitId) {
        try {
          const configRes = await api.get(`/ledger/rent-due-config/${unitId}`);
          if (configRes.data) {
            setRentConfig({
              rent_amount: configRes.data.rent_amount?.toString() || "",
              due_day: configRes.data.due_day?.toString() || "1",
              tenant_name: configRes.data.tenant_name || "",
              tenant_email: configRes.data.tenant_email || "",
              tenant_phone: configRes.data.tenant_phone || ""
            });
          }
        } catch {}
        
        try {
          const lateFeeRes = await api.get(`/ledger/late-fee-config/${unitId}`);
          if (lateFeeRes.data) {
            setLateFeeConfig({
              grace_period_days: lateFeeRes.data.grace_period_days?.toString() || "5",
              late_fee_type: lateFeeRes.data.late_fee_type || "flat",
              late_fee_amount: lateFeeRes.data.late_fee_amount?.toString() || "50",
              max_late_fee: lateFeeRes.data.max_late_fee?.toString() || ""
            });
          }
        } catch {}
      }
      
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [api, propertyId, unitId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchCalendar = useCallback(async () => {
    try {
      const res = await api.get(`/ledger/calendar?property_id=${propertyId}&month=${calendarMonth}&year=${calendarYear}`);
      setCalendarEvents(res.data.events || []);
    } catch (error) {
      console.error("Failed to fetch calendar:", error);
    }
  }, [api, propertyId, calendarMonth, calendarYear]);

  useEffect(() => {
    if (activeTab === "calendar") {
      fetchCalendar();
    }
  }, [activeTab, fetchCalendar]);

  const fetchRentRoll = useCallback(async () => {
    if (!propertyId) return;
    setRentRollLoading(true);
    try {
      const res = await api.get(`/ledger/rent-roll?property_id=${propertyId}&year=${rentRollYear}`);
      setRentRoll(res.data);
      
      // Also fetch deposits
      const depositsRes = await api.get(`/ledger/deposits-summary?property_id=${propertyId}`);
      setDepositsData(depositsRes.data);
    } catch (error) {
      console.error("Failed to fetch rent roll:", error);
    } finally {
      setRentRollLoading(false);
    }
  }, [api, propertyId, rentRollYear]);

  useEffect(() => {
    if (activeTab === "rent-roll") {
      fetchRentRoll();
    }
  }, [activeTab, fetchRentRoll]);

  const handleAddTransaction = async (e) => {
    e.preventDefault();
    try {
      await api.post("/ledger/transactions", {
        ...transactionForm,
        property_id: propertyId,
        unit_id: unitId || selectedUnitId !== "all" ? selectedUnitId : "property-wide",
        amount: parseFloat(transactionForm.amount)
      });
      toast.success("Transaction added");
      setTransactionDialogOpen(false);
      setTransactionForm({
        transaction_type: "income",
        category: "rent",
        amount: "",
        description: "",
        date: new Date().toISOString().split('T')[0],
        payment_method: "zelle",
        vendor: "",
        is_tax_deductible: false,
        notes: ""
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to add transaction");
    }
  };

  const handleEditUnit = (unit) => {
    setEditingUnit(unit);
    setEditUnitForm({
      unit_number: unit.unit_number || "",
      rent_amount: unit.rent_amount?.toString() || "",
      lease_start: unit.lease_start || "",
      lease_end: unit.lease_end || "",
      deposit: unit.deposit?.toString() || "",
      deposit_held: unit.deposit_held !== false,
      tenant_name: unit.tenant_name || "",
      tenant_email: unit.tenant_email || "",
      tenant_phone: unit.tenant_phone || "",
      is_occupied: unit.is_occupied || false,
      rent_due_day: unit.rent_due_day?.toString() || "1",
      late_fee_amount: unit.late_fee_amount?.toString() || "50",
      grace_period_days: unit.grace_period_days?.toString() || "5"
    });
    setEditUnitDialogOpen(true);
  };

  const handleSaveUnit = async () => {
    if (!editingUnit) return;
    try {
      await api.put(`/units/${editingUnit.unit_id || editingUnit.id}`, {
        property_id: propertyId,
        unit_number: editUnitForm.unit_number,
        rent_amount: parseFloat(editUnitForm.rent_amount) || 0,
        lease_start: editUnitForm.lease_start || null,
        lease_end: editUnitForm.lease_end || null,
        deposit: parseFloat(editUnitForm.deposit) || 0,
        deposit_held: editUnitForm.deposit_held,
        tenant_name: editUnitForm.tenant_name,
        tenant_email: editUnitForm.tenant_email,
        tenant_phone: editUnitForm.tenant_phone,
        is_occupied: editUnitForm.is_occupied,
        rent_due_day: parseInt(editUnitForm.rent_due_day) || 1,
        late_fee_amount: parseFloat(editUnitForm.late_fee_amount) || 50,
        grace_period_days: parseInt(editUnitForm.grace_period_days) || 5
      });
      toast.success("Unit updated successfully");
      setEditUnitDialogOpen(false);
      fetchRentRoll();
      fetchData();
    } catch (error) {
      toast.error("Failed to update unit");
    }
  };

  const handleRecordPayment = async (unitId, year, month, rentAmount) => {
    const amount = window.prompt(`Enter payment amount for ${new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}:`, rentAmount.toString());
    if (!amount) return;
    
    try {
      await api.post(`/ledger/rent-roll/record-payment?unit_id=${unitId}&year=${year}&month=${month}&amount=${amount}`);
      toast.success("Payment recorded");
      fetchRentRoll();
      fetchData();
    } catch (error) {
      toast.error("Failed to record payment");
    }
  };

  const handleDeleteTransaction = async (id) => {
    if (!window.confirm("Delete this transaction?")) return;
    try {
      await api.delete(`/ledger/transactions/${id}`);
      toast.success("Transaction deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete transaction");
    }
  };

  const handleReceiptOCR = async () => {
    if (!receiptUrl) {
      toast.error("Please enter receipt URL");
      return;
    }
    setOcrLoading(true);
    try {
      const res = await api.post(`/ledger/receipt-ocr?receipt_url=${encodeURIComponent(receiptUrl)}`);
      setOcrResult(res.data);
      if (res.data.confidence > 0) {
        // Pre-fill transaction form with OCR data
        setTransactionForm(prev => ({
          ...prev,
          transaction_type: "expense",
          category: res.data.category || "other",
          amount: res.data.amount?.toString() || "",
          description: res.data.description || "",
          date: res.data.date || prev.date,
          vendor: res.data.vendor || "",
          receipt_url: receiptUrl
        }));
        toast.success("Receipt processed! Review and submit.");
        setReceiptDialogOpen(false);
        setTransactionDialogOpen(true);
      } else {
        toast.error("Could not extract data from receipt");
      }
    } catch (error) {
      toast.error("Failed to process receipt");
    } finally {
      setOcrLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      // Save rent config
      await api.post("/ledger/rent-due-config", {
        unit_id: unitId,
        property_id: propertyId,
        rent_amount: parseFloat(rentConfig.rent_amount) || 0,
        due_day: parseInt(rentConfig.due_day) || 1,
        tenant_name: rentConfig.tenant_name,
        tenant_email: rentConfig.tenant_email,
        tenant_phone: rentConfig.tenant_phone
      });
      
      // Save late fee config
      await api.post("/ledger/late-fee-config", {
        unit_id: unitId,
        property_id: propertyId,
        grace_period_days: parseInt(lateFeeConfig.grace_period_days) || 5,
        late_fee_type: lateFeeConfig.late_fee_type,
        late_fee_amount: parseFloat(lateFeeConfig.late_fee_amount) || 50,
        max_late_fee: lateFeeConfig.max_late_fee ? parseFloat(lateFeeConfig.max_late_fee) : null
      });
      
      toast.success("Configuration saved");
      setConfigDialogOpen(false);
    } catch (error) {
      toast.error("Failed to save configuration");
    }
  };

  const handleGenerateReport = async () => {
    try {
      let url = `/ledger/profit-loss?property_id=${propertyId}&start_date=${reportStartDate}&end_date=${reportEndDate}`;
      if (unitId) url += `&unit_id=${unitId}`;
      const res = await api.get(url);
      setReportData(res.data);
    } catch (error) {
      toast.error("Failed to generate report");
    }
  };

  const handleSendReminder = async () => {
    if (!unitId) {
      toast.error("Select a specific unit to send reminder");
      return;
    }
    try {
      await api.post(`/ledger/send-reminder?unit_id=${unitId}`);
      toast.success("Reminder sent to tenant");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminder");
    }
  };

  const handleReconciliation = async () => {
    if (!bankBalance) {
      toast.error("Enter bank balance");
      return;
    }
    try {
      let url = `/ledger/bank-reconciliation?property_id=${propertyId}&month=${calendarMonth}&year=${calendarYear}&bank_balance=${bankBalance}`;
      if (unitId) url += `&unit_id=${unitId}`;
      const res = await api.get(url);
      setReconciliationData(res.data);
    } catch (error) {
      toast.error("Failed to reconcile");
    }
  };

  const handleGenerateAiReport = async (reportType) => {
    if (!reportType) {
      toast.error("Please select a report type");
      return;
    }
    setAiReportLoading(true);
    setAiReportData(null);
    try {
      let url = `/ledger/ai-report?report_type=${reportType}&property_id=${propertyId}&start_date=${aiReportStartDate}&end_date=${aiReportEndDate}`;
      if (unitId) url += `&unit_id=${unitId}`;
      const res = await api.post(url);
      setAiReportData(res.data);
      toast.success(`${res.data.report_name} generated!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate report");
    } finally {
      setAiReportLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!selectedReportType) {
      toast.error("Please select a report type first");
      return;
    }
    
    toast.info("Generating PDF...");
    try {
      let url = `/ledger/export-pdf?report_type=${selectedReportType}&property_id=${propertyId}&start_date=${aiReportStartDate}&end_date=${aiReportEndDate}`;
      if (unitId) url += `&unit_id=${unitId}`;
      
      const response = await api.post(url, null, { responseType: 'blob' });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Get filename from header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${selectedReportType}_report.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success("PDF downloaded!");
    } catch (error) {
      toast.error("Failed to generate PDF");
    }
  };

  const handleUnitChange = async (newUnitId) => {
    setSelectedUnitId(newUnitId);
    setLoading(true);
    try {
      // Fetch transactions for selected unit
      let url = `/ledger/transactions?property_id=${propertyId}`;
      if (newUnitId && newUnitId !== "all") url += `&unit_id=${newUnitId}`;
      const transRes = await api.get(url);
      setTransactions(transRes.data);
      
      // Update unit info
      if (newUnitId && newUnitId !== "all") {
        const foundUnit = allUnits.find(u => u.id === newUnitId);
        setUnit(foundUnit);
        
        // Fetch unit-specific configs
        try {
          const configRes = await api.get(`/ledger/rent-due-config/${newUnitId}`);
          if (configRes.data) {
            setRentConfig({
              rent_amount: configRes.data.rent_amount?.toString() || "",
              due_day: configRes.data.due_day?.toString() || "1",
              tenant_name: configRes.data.tenant_name || "",
              tenant_email: configRes.data.tenant_email || "",
              tenant_phone: configRes.data.tenant_phone || ""
            });
          }
        } catch {}
      } else {
        setUnit(null);
      }
    } catch (error) {
      toast.error("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals
  const totalIncome = transactions.filter(t => t.transaction_type === "income").reduce((sum, t) => sum + t.amount, 0);
  const totalExpenses = transactions.filter(t => t.transaction_type === "expense").reduce((sum, t) => sum + t.amount, 0);
  const netProfit = totalIncome - totalExpenses;
  
  // Group transactions by unit for property-wide view
  const transactionsByUnit = {};
  transactions.forEach(t => {
    const uid = t.unit_id || "property-wide";
    if (!transactionsByUnit[uid]) transactionsByUnit[uid] = [];
    transactionsByUnit[uid].push(t);
  });

  // Generate calendar days
  const getDaysInMonth = (month, year) => new Date(year, month, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month - 1, 1).getDay();
  const daysInMonth = getDaysInMonth(calendarMonth, calendarYear);
  const firstDay = getFirstDayOfMonth(calendarMonth, calendarYear);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Link to={`/real-estate/${propertyId}`}>
              <Button variant="outline" size="icon" className="border-white/10 rounded-sm">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
                {property?.name} Ledger
              </h1>
              <p className="text-zinc-500 text-sm">
                {selectedUnitId === "all" ? "All Units (Property-Wide)" : unit ? `Unit ${unit.unit_number}` : "Property-Wide"}
              </p>
            </div>
          </div>
          
          {/* Unit Selector */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Label className="text-zinc-400 text-sm">View:</Label>
              <Select value={selectedUnitId} onValueChange={handleUnitChange}>
                <SelectTrigger className="w-48 bg-black/50 border-white/10 rounded-sm" data-testid="unit-selector">
                  <SelectValue placeholder="Select view" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="all">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-primary" />
                      All Units (Property)
                    </div>
                  </SelectItem>
                  {allUnits.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      <div className="flex items-center gap-2">
                        <Home className="w-4 h-4 text-zinc-400" />
                        Unit {u.unit_number} {u.tenant_name && `- ${u.tenant_name}`}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        
        {/* Action Buttons Row */}
        <div className="flex items-center gap-2 flex-wrap">
          {selectedUnitId && selectedUnitId !== "all" && (
            <Button
              onClick={handleSendReminder}
              variant="outline"
              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10 rounded-sm"
              data-testid="send-reminder-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Reminder
            </Button>
          )}
          <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-white/20 rounded-sm" data-testid="config-btn" disabled={selectedUnitId === "all"}>
                <Calculator className="w-4 h-4 mr-2" />
                Configure
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-white" style={{ fontFamily: 'Manrope' }}>
                    Rent & Late Fee Configuration
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-6">
                  {/* Rent Config */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-zinc-300 border-b border-white/10 pb-2">Rent Settings</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Monthly Rent</Label>
                        <Input
                          type="number"
                          value={rentConfig.rent_amount}
                          onChange={(e) => setRentConfig({ ...rentConfig, rent_amount: e.target.value })}
                          placeholder="1500"
                          className="bg-black/50 border-white/10 rounded-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Due Day</Label>
                        <Select value={rentConfig.due_day} onValueChange={(v) => setRentConfig({ ...rentConfig, due_day: v })}>
                          <SelectTrigger className="bg-black/50 border-white/10 rounded-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A0A] border-white/10">
                            {[...Array(28)].map((_, i) => (
                              <SelectItem key={i + 1} value={(i + 1).toString()}>{i + 1}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400">Tenant Name</Label>
                      <Input
                        value={rentConfig.tenant_name}
                        onChange={(e) => setRentConfig({ ...rentConfig, tenant_name: e.target.value })}
                        placeholder="John Doe"
                        className="bg-black/50 border-white/10 rounded-sm"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Tenant Email</Label>
                        <Input
                          type="email"
                          value={rentConfig.tenant_email}
                          onChange={(e) => setRentConfig({ ...rentConfig, tenant_email: e.target.value })}
                          placeholder="tenant@email.com"
                          className="bg-black/50 border-white/10 rounded-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Tenant Phone</Label>
                        <Input
                          type="tel"
                          value={rentConfig.tenant_phone}
                          onChange={(e) => setRentConfig({ ...rentConfig, tenant_phone: e.target.value })}
                          placeholder="555-123-4567"
                          className="bg-black/50 border-white/10 rounded-sm"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Late Fee Config */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-zinc-300 border-b border-white/10 pb-2">Late Fee Settings</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Grace Period (Days)</Label>
                        <Input
                          type="number"
                          value={lateFeeConfig.grace_period_days}
                          onChange={(e) => setLateFeeConfig({ ...lateFeeConfig, grace_period_days: e.target.value })}
                          placeholder="5"
                          className="bg-black/50 border-white/10 rounded-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Fee Type</Label>
                        <Select value={lateFeeConfig.late_fee_type} onValueChange={(v) => setLateFeeConfig({ ...lateFeeConfig, late_fee_type: v })}>
                          <SelectTrigger className="bg-black/50 border-white/10 rounded-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A0A] border-white/10">
                            <SelectItem value="flat">Flat Fee</SelectItem>
                            <SelectItem value="percentage">Percentage</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-400">
                          {lateFeeConfig.late_fee_type === "flat" ? "Fee Amount ($)" : "Fee Percentage (%)"}
                        </Label>
                        <Input
                          type="number"
                          value={lateFeeConfig.late_fee_amount}
                          onChange={(e) => setLateFeeConfig({ ...lateFeeConfig, late_fee_amount: e.target.value })}
                          placeholder={lateFeeConfig.late_fee_type === "flat" ? "50" : "5"}
                          className="bg-black/50 border-white/10 rounded-sm"
                        />
                      </div>
                      {lateFeeConfig.late_fee_type === "percentage" && (
                        <div className="space-y-2">
                          <Label className="text-zinc-400">Max Fee ($)</Label>
                          <Input
                            type="number"
                            value={lateFeeConfig.max_late_fee}
                            onChange={(e) => setLateFeeConfig({ ...lateFeeConfig, max_late_fee: e.target.value })}
                            placeholder="100"
                            className="bg-black/50 border-white/10 rounded-sm"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <Button onClick={handleSaveConfig} className="w-full bg-primary text-black rounded-sm">
                    Save Configuration
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={receiptDialogOpen} onOpenChange={setReceiptDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10 rounded-sm" data-testid="scan-receipt-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Scan Receipt
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none">
                <DialogHeader>
                  <DialogTitle className="text-white" style={{ fontFamily: 'Manrope' }}>
                    AI Receipt Scanner
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-zinc-400">
                    Enter a URL to your receipt image and AI will extract the vendor, amount, date, and category.
                  </p>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Receipt Image URL</Label>
                    <Input
                      value={receiptUrl}
                      onChange={(e) => setReceiptUrl(e.target.value)}
                      placeholder="https://example.com/receipt.jpg"
                      className="bg-black/50 border-white/10 rounded-sm"
                    />
                  </div>
                  <Button 
                    onClick={handleReceiptOCR} 
                    disabled={ocrLoading}
                    className="w-full bg-orange-500 text-white hover:bg-orange-600 rounded-sm"
                  >
                    {ocrLoading ? "Processing..." : "Extract Data"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={transactionDialogOpen} onOpenChange={setTransactionDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary text-black rounded-sm" data-testid="add-transaction-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Transaction
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-white" style={{ fontFamily: 'Manrope' }}>
                    Add Transaction
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddTransaction} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Button
                      type="button"
                      onClick={() => setTransactionForm({ ...transactionForm, transaction_type: "income", category: "rent" })}
                      className={`${transactionForm.transaction_type === "income" ? "bg-green-500 text-white" : "bg-black/50 text-zinc-400 border border-white/10"}`}
                    >
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Income
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setTransactionForm({ ...transactionForm, transaction_type: "expense", category: "maintenance" })}
                      className={`${transactionForm.transaction_type === "expense" ? "bg-red-500 text-white" : "bg-black/50 text-zinc-400 border border-white/10"}`}
                    >
                      <TrendingDown className="w-4 h-4 mr-2" />
                      Expense
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Category</Label>
                      <Select 
                        value={transactionForm.category} 
                        onValueChange={(v) => setTransactionForm({ ...transactionForm, category: v })}
                      >
                        <SelectTrigger className="bg-black/50 border-white/10 rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A] border-white/10">
                          {TRANSACTION_CATEGORIES[transactionForm.transaction_type].map(cat => (
                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Amount</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={transactionForm.amount}
                        onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })}
                        placeholder="0.00"
                        required
                        className="bg-black/50 border-white/10 rounded-sm"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Description</Label>
                    <Input
                      value={transactionForm.description}
                      onChange={(e) => setTransactionForm({ ...transactionForm, description: e.target.value })}
                      placeholder="Brief description"
                      required
                      className="bg-black/50 border-white/10 rounded-sm"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Date</Label>
                      <Input
                        type="date"
                        value={transactionForm.date}
                        onChange={(e) => setTransactionForm({ ...transactionForm, date: e.target.value })}
                        required
                        className="bg-black/50 border-white/10 rounded-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Payment Method</Label>
                      <Select 
                        value={transactionForm.payment_method} 
                        onValueChange={(v) => setTransactionForm({ ...transactionForm, payment_method: v })}
                      >
                        <SelectTrigger className="bg-black/50 border-white/10 rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A] border-white/10">
                          {PAYMENT_METHODS.map(m => (
                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {transactionForm.transaction_type === "expense" && (
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Vendor</Label>
                      <Input
                        value={transactionForm.vendor}
                        onChange={(e) => setTransactionForm({ ...transactionForm, vendor: e.target.value })}
                        placeholder="Company/Contractor name"
                        className="bg-black/50 border-white/10 rounded-sm"
                      />
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <Label className="text-zinc-300">Tax Deductible</Label>
                    <Switch
                      checked={transactionForm.is_tax_deductible}
                      onCheckedChange={(v) => setTransactionForm({ ...transactionForm, is_tax_deductible: v })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Notes (Optional)</Label>
                    <Input
                      value={transactionForm.notes}
                      onChange={(e) => setTransactionForm({ ...transactionForm, notes: e.target.value })}
                      placeholder="Additional notes"
                      className="bg-black/50 border-white/10 rounded-sm"
                    />
                  </div>
                  
                  <Button type="submit" className="w-full bg-primary text-black rounded-sm">
                    Add Transaction
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wide">Total Income</p>
                  <p className="text-2xl font-bold text-green-400 font-mono mt-1">{formatCurrency(totalIncome)}</p>
                </div>
                <div className="w-10 h-10 bg-green-500/20 rounded flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wide">Total Expenses</p>
                  <p className="text-2xl font-bold text-red-400 font-mono mt-1">{formatCurrency(totalExpenses)}</p>
                </div>
                <div className="w-10 h-10 bg-red-500/20 rounded flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-red-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wide">Net Profit</p>
                  <p className={`text-2xl font-bold font-mono mt-1 ${netProfit >= 0 ? "text-primary" : "text-red-400"}`}>
                    {formatCurrency(netProfit)}
                  </p>
                </div>
                <div className={`w-10 h-10 rounded flex items-center justify-center ${netProfit >= 0 ? "bg-primary/20" : "bg-red-500/20"}`}>
                  <DollarSign className={`w-5 h-5 ${netProfit >= 0 ? "text-primary" : "text-red-400"}`} />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wide">Transactions</p>
                  <p className="text-2xl font-bold text-white font-mono mt-1">{transactions.length}</p>
                </div>
                <div className="w-10 h-10 bg-blue-500/20 rounded flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-black/50 border border-white/10 rounded-none p-1 flex-wrap">
            <TabsTrigger value="rent-roll" className="rounded-none data-[state=active]:bg-blue-500 data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />
              Rent Roll
            </TabsTrigger>
            <TabsTrigger value="ledger" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-black">
              <Receipt className="w-4 h-4 mr-2" />
              Ledger
            </TabsTrigger>
            <TabsTrigger value="calendar" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-black">
              <Calendar className="w-4 h-4 mr-2" />
              Calendar
            </TabsTrigger>
            <TabsTrigger value="reports" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-black">
              <FileText className="w-4 h-4 mr-2" />
              P&L Report
            </TabsTrigger>
            <TabsTrigger value="ai-reports" className="rounded-none data-[state=active]:bg-violet-500 data-[state=active]:text-white">
              <Bot className="w-4 h-4 mr-2" />
              AI Reports
            </TabsTrigger>
            <TabsTrigger value="reconcile" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-black">
              <Wallet className="w-4 h-4 mr-2" />
              Reconcile
            </TabsTrigger>
          </TabsList>

          {/* Rent Roll Tab */}
          <TabsContent value="rent-roll" className="mt-4 space-y-4">
            {/* Year Selector */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Label className="text-zinc-400">Year:</Label>
                <Select value={rentRollYear.toString()} onValueChange={(v) => setRentRollYear(parseInt(v))}>
                  <SelectTrigger className="w-28 bg-black/50 border-white/10 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    {[2024, 2025, 2026, 2027].map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={fetchRentRoll} className="border-white/10 rounded-sm">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
              
              {rentRoll && (
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-zinc-500">Collection Rate:</span>
                  <span className={`font-bold ${rentRoll.summary.collection_rate >= 90 ? "text-green-400" : rentRoll.summary.collection_rate >= 70 ? "text-yellow-400" : "text-red-400"}`}>
                    {rentRoll.summary.collection_rate.toFixed(1)}%
                  </span>
                </div>
              )}
            </div>

            {rentRollLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : rentRoll ? (
              <>
                {/* Deposits Summary */}
                {depositsData && depositsData.total_deposits_held > 0 && (
                  <Card className="bg-blue-500/10 border-blue-500/30 rounded-none">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Banknote className="w-5 h-5 text-blue-400" />
                          <span className="text-blue-300 font-medium">Security Deposits Held</span>
                        </div>
                        <span className="text-xl font-bold text-blue-400 font-mono">
                          {formatCurrency(depositsData.total_deposits_held)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Rent Roll Table */}
                <Card className="bg-[#0A0A0A] border-white/10 rounded-none overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-zinc-900 border-b border-white/10">
                          <th className="text-left p-3 text-xs text-zinc-400 font-medium sticky left-0 bg-zinc-900">Unit / Tenant</th>
                          <th className="text-left p-3 text-xs text-zinc-400 font-medium">Lease Period</th>
                          <th className="text-right p-3 text-xs text-zinc-400 font-medium">Rent</th>
                          <th className="text-right p-3 text-xs text-zinc-400 font-medium">Deposit</th>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
                            <th key={m} className="text-center p-2 text-xs text-zinc-500 font-medium min-w-[60px]">
                              {new Date(rentRollYear, m - 1).toLocaleDateString('en-US', { month: 'short' })}
                            </th>
                          ))}
                          <th className="p-3"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {rentRoll.rent_roll.map((unit) => (
                          <tr key={unit.unit_id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="p-3 sticky left-0 bg-[#0A0A0A]">
                              <div className="flex items-center gap-2">
                                {unit.is_occupied ? (
                                  <CheckCircle className="w-4 h-4 text-green-400" />
                                ) : (
                                  <XCircle className="w-4 h-4 text-zinc-500" />
                                )}
                                <div>
                                  <p className="font-medium text-white">Unit {unit.unit_number}</p>
                                  <p className="text-xs text-zinc-500">{unit.tenant_name || "Vacant"}</p>
                                  {unit.tenant_email && (
                                    <p className="text-xs text-zinc-600">{unit.tenant_email}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="p-3">
                              {unit.lease_start && unit.lease_end ? (
                                <div className="text-xs">
                                  <p className="text-zinc-300">{formatDate(unit.lease_start)}</p>
                                  <p className="text-zinc-500">to {formatDate(unit.lease_end)}</p>
                                </div>
                              ) : (
                                <span className="text-xs text-zinc-600">No lease</span>
                              )}
                            </td>
                            <td className="p-3 text-right">
                              <span className="text-sm font-mono text-white">{formatCurrency(unit.rent_amount)}</span>
                            </td>
                            <td className="p-3 text-right">
                              <span className={`text-sm font-mono ${unit.deposit_held ? "text-blue-400" : "text-zinc-500"}`}>
                                {formatCurrency(unit.deposit)}
                                {unit.deposit_held && <span className="text-xs ml-1">✓</span>}
                              </span>
                            </td>
                            {unit.monthly_status.map((ms, idx) => {
                              const statusColors = {
                                paid: "bg-green-500/30 text-green-400 border-green-500/50",
                                partial: "bg-yellow-500/30 text-yellow-400 border-yellow-500/50",
                                pending: "bg-zinc-800 text-zinc-500 border-zinc-700",
                                late: "bg-orange-500/30 text-orange-400 border-orange-500/50",
                                overdue: "bg-red-500/30 text-red-400 border-red-500/50"
                              };
                              return (
                                <td key={idx} className="p-1 text-center">
                                  <button
                                    onClick={() => handleRecordPayment(unit.unit_id, ms.year, ms.month, unit.rent_amount)}
                                    className={`w-full p-1 text-xs font-mono border rounded cursor-pointer hover:opacity-80 ${statusColors[ms.status] || statusColors.pending}`}
                                    title={`${ms.status}: $${ms.amount_paid} / $${ms.rent_amount}`}
                                  >
                                    {ms.status === "paid" ? "✓" : ms.status === "partial" ? `$${ms.amount_paid}` : ms.status === "overdue" ? "!" : "—"}
                                  </button>
                                </td>
                              );
                            })}
                            <td className="p-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditUnit(unit)}
                                className="text-zinc-400 hover:text-white"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>

                {/* Legend */}
                <div className="flex items-center gap-6 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-500/30 border border-green-500/50 rounded"></div>
                    <span className="text-zinc-400">Paid</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-yellow-500/30 border border-yellow-500/50 rounded"></div>
                    <span className="text-zinc-400">Partial</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-orange-500/30 border border-orange-500/50 rounded"></div>
                    <span className="text-zinc-400">Late</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-red-500/30 border border-red-500/50 rounded"></div>
                    <span className="text-zinc-400">Overdue</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-zinc-800 border border-zinc-700 rounded"></div>
                    <span className="text-zinc-400">Pending</span>
                  </div>
                </div>

                {/* Summary Card */}
                <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-zinc-500">Total Units</p>
                        <p className="text-xl font-bold text-white">{rentRoll.summary.total_units}</p>
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500">Occupied</p>
                        <p className="text-xl font-bold text-green-400">{rentRoll.summary.occupied_units}</p>
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500">Total Rent Due ({rentRollYear})</p>
                        <p className="text-xl font-bold text-white font-mono">{formatCurrency(rentRoll.summary.total_rent_due)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500">Total Collected</p>
                        <p className="text-xl font-bold text-green-400 font-mono">{formatCurrency(rentRoll.summary.total_collected)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <div className="text-center py-12 text-zinc-500">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No rent roll data available</p>
              </div>
            )}
          </TabsContent>

          {/* Edit Unit Dialog */}
          <Dialog open={editUnitDialogOpen} onOpenChange={setEditUnitDialogOpen}>
            <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white" style={{ fontFamily: 'Manrope' }}>
                  Edit Unit {editingUnit?.unit_number}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Unit Number</Label>
                    <Input
                      value={editUnitForm.unit_number}
                      onChange={(e) => setEditUnitForm({ ...editUnitForm, unit_number: e.target.value })}
                      className="bg-black/50 border-white/10 rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Monthly Rent</Label>
                    <Input
                      type="number"
                      value={editUnitForm.rent_amount}
                      onChange={(e) => setEditUnitForm({ ...editUnitForm, rent_amount: e.target.value })}
                      className="bg-black/50 border-white/10 rounded-sm"
                    />
                  </div>
                </div>
                
                <div className="border-t border-white/10 pt-4">
                  <h4 className="text-sm font-medium text-zinc-300 mb-3">Lease Holder</h4>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label className="text-zinc-400">Tenant Name</Label>
                      <Input
                        value={editUnitForm.tenant_name}
                        onChange={(e) => setEditUnitForm({ ...editUnitForm, tenant_name: e.target.value })}
                        placeholder="John Doe"
                        className="bg-black/50 border-white/10 rounded-sm"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Email</Label>
                        <Input
                          type="email"
                          value={editUnitForm.tenant_email}
                          onChange={(e) => setEditUnitForm({ ...editUnitForm, tenant_email: e.target.value })}
                          className="bg-black/50 border-white/10 rounded-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Phone</Label>
                        <Input
                          value={editUnitForm.tenant_phone}
                          onChange={(e) => setEditUnitForm({ ...editUnitForm, tenant_phone: e.target.value })}
                          className="bg-black/50 border-white/10 rounded-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-white/10 pt-4">
                  <h4 className="text-sm font-medium text-zinc-300 mb-3">Lease Period</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-400">Start Date</Label>
                      <Input
                        type="date"
                        value={editUnitForm.lease_start}
                        onChange={(e) => setEditUnitForm({ ...editUnitForm, lease_start: e.target.value })}
                        className="bg-black/50 border-white/10 rounded-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400">End Date</Label>
                      <Input
                        type="date"
                        value={editUnitForm.lease_end}
                        onChange={(e) => setEditUnitForm({ ...editUnitForm, lease_end: e.target.value })}
                        className="bg-black/50 border-white/10 rounded-sm"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-white/10 pt-4">
                  <h4 className="text-sm font-medium text-zinc-300 mb-3">Deposit & Settings</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-400">Security Deposit</Label>
                      <Input
                        type="number"
                        value={editUnitForm.deposit}
                        onChange={(e) => setEditUnitForm({ ...editUnitForm, deposit: e.target.value })}
                        className="bg-black/50 border-white/10 rounded-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400">Rent Due Day</Label>
                      <Select value={editUnitForm.rent_due_day} onValueChange={(v) => setEditUnitForm({ ...editUnitForm, rent_due_day: v })}>
                        <SelectTrigger className="bg-black/50 border-white/10 rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A] border-white/10">
                          {[...Array(28)].map((_, i) => (
                            <SelectItem key={i + 1} value={(i + 1).toString()}>{i + 1}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div className="space-y-2">
                      <Label className="text-zinc-400">Late Fee</Label>
                      <Input
                        type="number"
                        value={editUnitForm.late_fee_amount}
                        onChange={(e) => setEditUnitForm({ ...editUnitForm, late_fee_amount: e.target.value })}
                        className="bg-black/50 border-white/10 rounded-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400">Grace Period (Days)</Label>
                      <Input
                        type="number"
                        value={editUnitForm.grace_period_days}
                        onChange={(e) => setEditUnitForm({ ...editUnitForm, grace_period_days: e.target.value })}
                        className="bg-black/50 border-white/10 rounded-sm"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-6 mt-4">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={editUnitForm.is_occupied}
                        onCheckedChange={(v) => setEditUnitForm({ ...editUnitForm, is_occupied: v })}
                      />
                      <Label className="text-zinc-300">Occupied</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={editUnitForm.deposit_held}
                        onCheckedChange={(v) => setEditUnitForm({ ...editUnitForm, deposit_held: v })}
                      />
                      <Label className="text-zinc-300">Deposit Held</Label>
                    </div>
                  </div>
                </div>
                
                <Button onClick={handleSaveUnit} className="w-full bg-primary text-black rounded-sm">
                  Save Changes
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Ledger Tab */}
          <TabsContent value="ledger" className="mt-4">
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardContent className="p-0">
                {transactions.length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">
                    <Receipt className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No transactions yet. Add your first transaction!</p>
                  </div>
                ) : selectedUnitId === "all" && allUnits.length > 0 ? (
                  /* Property-wide view: Group by unit */
                  <div className="space-y-4 p-4">
                    {/* Property-wide transactions first */}
                    {transactionsByUnit["property-wide"]?.length > 0 && (
                      <div className="border border-white/10 rounded">
                        <div className="bg-primary/10 p-3 border-b border-white/10">
                          <h3 className="font-bold text-primary flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            Property-Wide Expenses
                          </h3>
                        </div>
                        <div className="divide-y divide-white/5">
                          {transactionsByUnit["property-wide"].map((t) => (
                            <div key={t.id} className="p-3 hover:bg-white/5 transition-colors flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded flex items-center justify-center ${
                                  t.transaction_type === "income" ? "bg-green-500/20" : "bg-red-500/20"
                                }`}>
                                  {t.transaction_type === "income" ? (
                                    <TrendingUp className="w-4 h-4 text-green-400" />
                                  ) : (
                                    <TrendingDown className="w-4 h-4 text-red-400" />
                                  )}
                                </div>
                                <div>
                                  <p className="font-medium text-white text-sm">{t.description}</p>
                                  <p className="text-xs text-zinc-500">{formatDate(t.date)} • {t.category}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <p className={`font-bold font-mono text-sm ${
                                  t.transaction_type === "income" ? "text-green-400" : "text-red-400"
                                }`}>
                                  {t.transaction_type === "income" ? "+" : "-"}{formatCurrency(t.amount)}
                                </p>
                                <Button variant="ghost" size="icon" onClick={() => handleDeleteTransaction(t.id)} className="text-zinc-500 hover:text-red-400 h-8 w-8">
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Unit-specific transactions */}
                    {allUnits.map(u => {
                      const unitTrans = transactionsByUnit[u.id] || [];
                      if (unitTrans.length === 0) return null;
                      const unitIncome = unitTrans.filter(t => t.transaction_type === "income").reduce((s, t) => s + t.amount, 0);
                      const unitExpenses = unitTrans.filter(t => t.transaction_type === "expense").reduce((s, t) => s + t.amount, 0);
                      
                      return (
                        <div key={u.id} className="border border-white/10 rounded">
                          <div className="bg-zinc-900 p-3 border-b border-white/10 flex items-center justify-between">
                            <h3 className="font-bold text-white flex items-center gap-2">
                              <Home className="w-4 h-4 text-zinc-400" />
                              Unit {u.unit_number} {u.tenant_name && <span className="font-normal text-zinc-500">- {u.tenant_name}</span>}
                            </h3>
                            <div className="flex items-center gap-4 text-xs">
                              <span className="text-green-400">+{formatCurrency(unitIncome)}</span>
                              <span className="text-red-400">-{formatCurrency(unitExpenses)}</span>
                              <span className={unitIncome - unitExpenses >= 0 ? "text-primary font-bold" : "text-red-400 font-bold"}>
                                Net: {formatCurrency(unitIncome - unitExpenses)}
                              </span>
                            </div>
                          </div>
                          <div className="divide-y divide-white/5">
                            {unitTrans.map((t) => (
                              <div key={t.id} className="p-3 hover:bg-white/5 transition-colors flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded flex items-center justify-center ${
                                    t.transaction_type === "income" ? "bg-green-500/20" : "bg-red-500/20"
                                  }`}>
                                    {t.transaction_type === "income" ? (
                                      <TrendingUp className="w-4 h-4 text-green-400" />
                                    ) : (
                                      <TrendingDown className="w-4 h-4 text-red-400" />
                                    )}
                                  </div>
                                  <div>
                                    <p className="font-medium text-white text-sm">{t.description}</p>
                                    <p className="text-xs text-zinc-500">{formatDate(t.date)} • {t.category} {t.vendor && `• ${t.vendor}`}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <div className="text-right">
                                    <p className={`font-bold font-mono text-sm ${
                                      t.transaction_type === "income" ? "text-green-400" : "text-red-400"
                                    }`}>
                                      {t.transaction_type === "income" ? "+" : "-"}{formatCurrency(t.amount)}
                                    </p>
                                    {t.is_tax_deductible && <span className="text-xs text-blue-400">Tax Ded.</span>}
                                  </div>
                                  <Button variant="ghost" size="icon" onClick={() => handleDeleteTransaction(t.id)} className="text-zinc-500 hover:text-red-400 h-8 w-8">
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* Single unit view */
                  <div className="divide-y divide-white/5">
                    {transactions.map((t) => (
                      <div key={t.id} className="p-4 hover:bg-white/5 transition-colors flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded flex items-center justify-center ${
                            t.transaction_type === "income" ? "bg-green-500/20" : "bg-red-500/20"
                          }`}>
                            {t.transaction_type === "income" ? (
                              <TrendingUp className="w-5 h-5 text-green-400" />
                            ) : (
                              <TrendingDown className="w-5 h-5 text-red-400" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-white">{t.description}</p>
                            <p className="text-xs text-zinc-500">
                              {formatDate(t.date)} • {t.category} • {t.payment_method}
                              {t.vendor && ` • ${t.vendor}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className={`font-bold font-mono ${
                              t.transaction_type === "income" ? "text-green-400" : "text-red-400"
                            }`}>
                              {t.transaction_type === "income" ? "+" : "-"}{formatCurrency(t.amount)}
                            </p>
                            {t.is_tax_deductible && (
                              <span className="text-xs text-blue-400">Tax Deductible</span>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteTransaction(t.id)}
                            className="text-zinc-500 hover:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar" className="mt-4">
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardHeader className="flex flex-row items-center justify-between">
                <div className="flex items-center gap-4">
                  <Button variant="outline" size="icon" onClick={() => {
                    if (calendarMonth === 1) {
                      setCalendarMonth(12);
                      setCalendarYear(calendarYear - 1);
                    } else {
                      setCalendarMonth(calendarMonth - 1);
                    }
                  }} className="border-white/10 rounded-sm">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <CardTitle className="text-lg text-white" style={{ fontFamily: 'Manrope' }}>
                    {new Date(calendarYear, calendarMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </CardTitle>
                  <Button variant="outline" size="icon" onClick={() => {
                    if (calendarMonth === 12) {
                      setCalendarMonth(1);
                      setCalendarYear(calendarYear + 1);
                    } else {
                      setCalendarMonth(calendarMonth + 1);
                    }
                  }} className="border-white/10 rounded-sm">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
                <Button variant="outline" onClick={fetchCalendar} className="border-white/10 rounded-sm">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </CardHeader>
              <CardContent>
                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="p-2 text-center text-xs text-zinc-500 font-medium">
                      {day}
                    </div>
                  ))}
                  {[...Array(firstDay)].map((_, i) => (
                    <div key={`empty-${i}`} className="p-2" />
                  ))}
                  {[...Array(daysInMonth)].map((_, i) => {
                    const day = i + 1;
                    const dateStr = `${calendarYear}-${String(calendarMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const dayEvents = calendarEvents.filter(e => e.date === dateStr);
                    const hasRentDue = dayEvents.some(e => e.type === "rent_due");
                    const hasTransaction = dayEvents.some(e => e.type === "transaction");
                    const isOverdue = dayEvents.some(e => e.status === "overdue");
                    const isPaid = dayEvents.some(e => e.status === "paid" || e.paid);
                    
                    return (
                      <div 
                        key={day} 
                        className={`p-2 min-h-[80px] border border-white/5 ${
                          hasRentDue ? (isPaid ? "bg-green-500/10" : isOverdue ? "bg-red-500/10" : "bg-yellow-500/10") : ""
                        }`}
                      >
                        <div className="text-sm text-zinc-400">{day}</div>
                        {dayEvents.slice(0, 2).map((event, idx) => (
                          <div 
                            key={idx} 
                            className={`text-xs mt-1 p-1 rounded truncate ${
                              event.type === "rent_due" 
                                ? event.paid ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"
                                : event.transaction_type === "income" 
                                  ? "bg-green-500/20 text-green-400" 
                                  : "bg-red-500/20 text-red-400"
                            }`}
                          >
                            {event.type === "rent_due" 
                              ? `Rent Due: ${event.unit_name}` 
                              : `${event.category}: ${formatCurrency(event.amount)}`
                            }
                          </div>
                        ))}
                        {dayEvents.length > 2 && (
                          <div className="text-xs text-zinc-500 mt-1">+{dayEvents.length - 2} more</div>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Legend */}
                <div className="flex items-center gap-6 mt-4 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-yellow-500/30 rounded"></div>
                    <span className="text-zinc-400">Rent Due</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500/30 rounded"></div>
                    <span className="text-zinc-400">Paid / Income</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500/30 rounded"></div>
                    <span className="text-zinc-400">Overdue / Expense</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* P&L Report Tab */}
          <TabsContent value="reports" className="mt-4">
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardHeader>
                <CardTitle className="text-lg text-white" style={{ fontFamily: 'Manrope' }}>
                  Profit & Loss Report
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-end gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Start Date</Label>
                    <Input
                      type="date"
                      value={reportStartDate}
                      onChange={(e) => setReportStartDate(e.target.value)}
                      className="bg-black/50 border-white/10 rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">End Date</Label>
                    <Input
                      type="date"
                      value={reportEndDate}
                      onChange={(e) => setReportEndDate(e.target.value)}
                      className="bg-black/50 border-white/10 rounded-sm"
                    />
                  </div>
                  <Button onClick={handleGenerateReport} className="bg-primary text-black rounded-sm">
                    <FileText className="w-4 h-4 mr-2" />
                    Generate Report
                  </Button>
                </div>
                
                {reportData && (
                  <div className="space-y-6 mt-6">
                    {/* Summary */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 bg-green-500/10 border border-green-500/30 rounded">
                        <p className="text-xs text-green-400 uppercase">Total Income</p>
                        <p className="text-2xl font-bold text-green-400 font-mono">{formatCurrency(reportData.total_income)}</p>
                      </div>
                      <div className="p-4 bg-red-500/10 border border-red-500/30 rounded">
                        <p className="text-xs text-red-400 uppercase">Total Expenses</p>
                        <p className="text-2xl font-bold text-red-400 font-mono">{formatCurrency(reportData.total_expenses)}</p>
                      </div>
                      <div className={`p-4 border rounded ${reportData.net_profit >= 0 ? "bg-primary/10 border-primary/30" : "bg-red-500/10 border-red-500/30"}`}>
                        <p className={`text-xs uppercase ${reportData.net_profit >= 0 ? "text-primary" : "text-red-400"}`}>Net Profit</p>
                        <p className={`text-2xl font-bold font-mono ${reportData.net_profit >= 0 ? "text-primary" : "text-red-400"}`}>
                          {formatCurrency(reportData.net_profit)}
                        </p>
                      </div>
                    </div>
                    
                    {/* Breakdown */}
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-sm font-medium text-zinc-300 mb-3">Income Breakdown</h4>
                        <div className="space-y-2">
                          {Object.entries(reportData.income_breakdown).map(([cat, amt]) => (
                            <div key={cat} className="flex justify-between text-sm">
                              <span className="text-zinc-400 capitalize">{cat.replace('_', ' ')}</span>
                              <span className="text-green-400 font-mono">{formatCurrency(amt)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-zinc-300 mb-3">Expense Breakdown</h4>
                        <div className="space-y-2">
                          {Object.entries(reportData.expense_breakdown).map(([cat, amt]) => (
                            <div key={cat} className="flex justify-between text-sm">
                              <span className="text-zinc-400 capitalize">{cat.replace('_', ' ')}</span>
                              <span className="text-red-400 font-mono">{formatCurrency(amt)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reconciliation Tab */}
          <TabsContent value="reconcile" className="mt-4">
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardHeader>
                <CardTitle className="text-lg text-white" style={{ fontFamily: 'Manrope' }}>
                  Bank Reconciliation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-zinc-400">
                  Compare your bank statement balance with the ledger to ensure all transactions are recorded.
                </p>
                <div className="flex items-end gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Month</Label>
                    <Select value={calendarMonth.toString()} onValueChange={(v) => setCalendarMonth(parseInt(v))}>
                      <SelectTrigger className="bg-black/50 border-white/10 rounded-sm w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/10">
                        {[...Array(12)].map((_, i) => (
                          <SelectItem key={i + 1} value={(i + 1).toString()}>
                            {new Date(2000, i).toLocaleDateString('en-US', { month: 'long' })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Year</Label>
                    <Input
                      type="number"
                      value={calendarYear}
                      onChange={(e) => setCalendarYear(parseInt(e.target.value))}
                      className="bg-black/50 border-white/10 rounded-sm w-24"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Bank Statement Balance</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={bankBalance}
                      onChange={(e) => setBankBalance(e.target.value)}
                      placeholder="Enter ending balance"
                      className="bg-black/50 border-white/10 rounded-sm w-48"
                    />
                  </div>
                  <Button onClick={handleReconciliation} className="bg-primary text-black rounded-sm">
                    <Calculator className="w-4 h-4 mr-2" />
                    Reconcile
                  </Button>
                </div>
                
                {reconciliationData && (
                  <div className="mt-6 space-y-4">
                    <div className={`p-4 border rounded flex items-center gap-4 ${
                      reconciliationData.reconciled ? "bg-green-500/10 border-green-500/30" : "bg-yellow-500/10 border-yellow-500/30"
                    }`}>
                      {reconciliationData.reconciled ? (
                        <CheckCircle className="w-8 h-8 text-green-400" />
                      ) : (
                        <AlertTriangle className="w-8 h-8 text-yellow-400" />
                      )}
                      <div>
                        <p className={`font-bold ${reconciliationData.reconciled ? "text-green-400" : "text-yellow-400"}`}>
                          {reconciliationData.message}
                        </p>
                        <p className="text-sm text-zinc-400">
                          Bank: {formatCurrency(reconciliationData.bank_balance)} | 
                          Ledger: {formatCurrency(reconciliationData.ledger_balance)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-3 bg-black/30 border border-white/5 rounded">
                        <p className="text-xs text-zinc-500">Ledger Income</p>
                        <p className="text-lg font-bold text-green-400 font-mono">{formatCurrency(reconciliationData.ledger_income)}</p>
                      </div>
                      <div className="p-3 bg-black/30 border border-white/5 rounded">
                        <p className="text-xs text-zinc-500">Ledger Expenses</p>
                        <p className="text-lg font-bold text-red-400 font-mono">{formatCurrency(reconciliationData.ledger_expenses)}</p>
                      </div>
                      <div className="p-3 bg-black/30 border border-white/5 rounded">
                        <p className="text-xs text-zinc-500">Difference</p>
                        <p className={`text-lg font-bold font-mono ${
                          Math.abs(reconciliationData.difference) < 0.01 ? "text-green-400" : "text-yellow-400"
                        }`}>
                          {formatCurrency(reconciliationData.difference)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI Reports Tab */}
          <TabsContent value="ai-reports" className="mt-4">
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Sparkles className="w-5 h-5 text-violet-400" />
                  AI-Powered Financial Reports
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-sm text-zinc-400">
                  Generate professional financial reports using AI. Select a report type and date range below.
                </p>
                
                {/* Date Range Selection */}
                <div className="flex items-end gap-4 flex-wrap">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Start Date</Label>
                    <Input
                      type="date"
                      value={aiReportStartDate}
                      onChange={(e) => setAiReportStartDate(e.target.value)}
                      className="bg-black/50 border-white/10 rounded-sm w-40"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">End Date</Label>
                    <Input
                      type="date"
                      value={aiReportEndDate}
                      onChange={(e) => setAiReportEndDate(e.target.value)}
                      className="bg-black/50 border-white/10 rounded-sm w-40"
                    />
                  </div>
                </div>

                {/* Report Type Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {AI_REPORT_TYPES.map((report) => {
                    const Icon = report.icon;
                    const isSelected = selectedReportType === report.value;
                    return (
                      <button
                        key={report.value}
                        onClick={() => setSelectedReportType(report.value)}
                        className={`p-4 border text-left transition-all ${
                          isSelected 
                            ? "border-violet-500 bg-violet-500/10" 
                            : "border-white/10 hover:border-white/20 bg-black/30"
                        }`}
                        data-testid={`report-type-${report.value}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded flex items-center justify-center ${
                            isSelected ? "bg-violet-500/20" : "bg-white/5"
                          }`}>
                            <Icon className={`w-5 h-5 ${isSelected ? "text-violet-400" : "text-zinc-400"}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium text-sm ${isSelected ? "text-violet-300" : "text-white"}`}>
                              {report.label}
                            </p>
                            <p className="text-xs text-zinc-500 mt-0.5 truncate">
                              {report.description}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Generate Buttons */}
                <div className="flex items-center gap-3">
                  <Button
                    onClick={() => handleGenerateAiReport(selectedReportType)}
                    disabled={!selectedReportType || aiReportLoading}
                    className="bg-violet-500 text-white hover:bg-violet-600 rounded-sm"
                    data-testid="generate-ai-report-btn"
                  >
                    {aiReportLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                        Generating Report...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Preview {selectedReportType ? AI_REPORT_TYPES.find(r => r.value === selectedReportType)?.label : "Report"}
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={handleDownloadPdf}
                    disabled={!selectedReportType || aiReportLoading}
                    variant="outline"
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10 rounded-sm"
                    data-testid="direct-pdf-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Direct PDF Download
                  </Button>
                </div>

                {/* AI Report Result */}
                {aiReportData && (
                  <div className="space-y-4 mt-6">
                    {/* Report Header */}
                    <div className="p-4 bg-violet-500/10 border border-violet-500/30 rounded">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="text-lg font-bold text-violet-300">{aiReportData.report_name}</h3>
                          <p className="text-xs text-zinc-500">
                            {aiReportData.property_name} • {formatDate(aiReportData.period_start)} - {formatDate(aiReportData.period_end)}
                          </p>
                        </div>
                        <div className="text-right text-xs text-zinc-500">
                          Generated: {formatDate(aiReportData.generated_at)}
                        </div>
                      </div>
                      
                      {/* Summary Stats */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                        <div className="p-2 bg-black/30 rounded">
                          <p className="text-xs text-zinc-500">Total Income</p>
                          <p className="text-sm font-bold text-green-400 font-mono">{formatCurrency(aiReportData.summary.total_income)}</p>
                        </div>
                        <div className="p-2 bg-black/30 rounded">
                          <p className="text-xs text-zinc-500">Total Expenses</p>
                          <p className="text-sm font-bold text-red-400 font-mono">{formatCurrency(aiReportData.summary.total_expenses)}</p>
                        </div>
                        <div className="p-2 bg-black/30 rounded">
                          <p className="text-xs text-zinc-500">Net Income</p>
                          <p className={`text-sm font-bold font-mono ${aiReportData.summary.net_income >= 0 ? "text-primary" : "text-red-400"}`}>
                            {formatCurrency(aiReportData.summary.net_income)}
                          </p>
                        </div>
                        <div className="p-2 bg-black/30 rounded">
                          <p className="text-xs text-zinc-500">Tax Deductible</p>
                          <p className="text-sm font-bold text-blue-400 font-mono">{formatCurrency(aiReportData.summary.tax_deductible)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Report Content */}
                    <Card className="bg-black/50 border-white/10 rounded-none">
                      <CardContent className="p-6">
                        <div className="prose prose-invert prose-sm max-w-none">
                          <pre className="whitespace-pre-wrap text-sm text-zinc-300 font-sans leading-relaxed bg-transparent p-0 m-0">
                            {aiReportData.report_content}
                          </pre>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Actions */}
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        className="border-white/10 rounded-sm"
                        onClick={() => {
                          const content = `${aiReportData.report_name}\n${'='.repeat(50)}\n\nProperty: ${aiReportData.property_name}\nPeriod: ${aiReportData.period_start} to ${aiReportData.period_end}\nGenerated: ${aiReportData.generated_at}\n\n${aiReportData.report_content}`;
                          navigator.clipboard.writeText(content);
                          toast.success("Report copied to clipboard");
                        }}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Copy Text
                      </Button>
                      <Button
                        onClick={handleDownloadPdf}
                        className="bg-red-600 text-white hover:bg-red-700 rounded-sm"
                        data-testid="download-pdf-btn"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Download PDF
                      </Button>
                      <Button
                        variant="outline"
                        className="border-white/10 rounded-sm"
                        onClick={() => {
                          setSelectedReportType("");
                          setAiReportData(null);
                        }}
                      >
                        Generate Another Report
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
